import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/auth/AuthPages.css';

/**
 * LoginPage - Email/password and OAuth login
 */
export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Validate redirect to prevent open redirect attacks
  const getSafeRedirect = (redirect: string | null): string => {
    if (!redirect) return '/';
    // Only allow relative paths starting with single / (not // which could be protocol-relative URL)
    if (redirect.startsWith('/') && !redirect.startsWith('//')) {
      // Block URLs with embedded schemes (javascript:, data:, etc.)
      if (redirect.includes(':')) {
        return '/';
      }
      return redirect;
    }
    return '/';
  };
  const redirect = getSafeRedirect(searchParams.get('redirect'));

  const {
    loginWithEmail,
    loginWithMicrosoft,
    loginWithGoogle,
    isLoggingIn,
    error,
    clearError
  } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});

  const validateForm = (): boolean => {
    const errors: { email?: string; password?: string } = {};

    if (!email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      errors.email = 'Email invalide';
    }

    if (!password) {
      errors.password = 'Le mot de passe est requis';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();

    if (!validateForm()) return;

    const result = await loginWithEmail(email, password);

    if (result.success) {
      navigate(redirect, { replace: true });
    } else if (result.requiresVerification) {
      navigate(`/verify-email?email=${encodeURIComponent(email)}&pending=true`);
    }
  };

  const handleMicrosoftLogin = () => {
    clearError();
    loginWithMicrosoft();
  };

  const handleGoogleLogin = () => {
    clearError();
    loginWithGoogle();
  };

  return (
    <div className="auth-page">
      <div className="auth-box">
        <header className="auth-header">
          <h1 className="auth-logo">Corpus</h1>
          <p className="auth-subtitle">Connectez-vous pour continuer</p>
        </header>

        {error && (
          <div className="auth-alert error" role="alert">
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
            </svg>
            <div className="auth-alert-content">
              <p className="auth-alert-title">Erreur de connexion</p>
              <p className="auth-alert-message">{error}</p>
            </div>
          </div>
        )}

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label className="auth-label" htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              className={`auth-input ${fieldErrors.email ? 'error' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="vous@exemple.com"
              autoComplete="email"
              disabled={isLoggingIn}
            />
            {fieldErrors.email && (
              <p className="auth-field-error">{fieldErrors.email}</p>
            )}
          </div>

          <div className="auth-field">
            <label className="auth-label" htmlFor="password">Mot de passe</label>
            <div className="auth-password-field">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`auth-input ${fieldErrors.password ? 'error' : ''}`}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Votre mot de passe"
                autoComplete="current-password"
                disabled={isLoggingIn}
              />
              <button
                type="button"
                className="auth-password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              >
                {showPassword ? (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {fieldErrors.password && (
              <p className="auth-field-error">{fieldErrors.password}</p>
            )}
          </div>

          <button type="submit" className="auth-submit" disabled={isLoggingIn}>
            {isLoggingIn ? (
              <>
                <span className="auth-spinner" />
                Connexion...
              </>
            ) : (
              'Se connecter'
            )}
          </button>
        </form>

        <div className="auth-divider">
          <div className="auth-divider-line" />
          <span className="auth-divider-text">ou</span>
          <div className="auth-divider-line" />
        </div>

        <div className="auth-oauth-buttons">
          <button
            type="button"
            className="auth-oauth-button"
            onClick={handleMicrosoftLogin}
            disabled={isLoggingIn}
          >
            <svg className="auth-oauth-icon" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F35325" />
              <rect x="11" y="1" width="9" height="9" fill="#81BC06" />
              <rect x="1" y="11" width="9" height="9" fill="#05A6F0" />
              <rect x="11" y="11" width="9" height="9" fill="#FFBA08" />
            </svg>
            Continuer avec Microsoft
          </button>

          <button
            type="button"
            className="auth-oauth-button"
            onClick={handleGoogleLogin}
            disabled={isLoggingIn}
          >
            <svg className="auth-oauth-icon" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continuer avec Google
          </button>
        </div>

        <div className="auth-links">
          <Link to="/forgot-password" className="auth-link">
            Mot de passe oublié ?
          </Link>
          <p className="auth-text">
            Pas encore de compte ? <Link to="/register">S'inscrire</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
